import type {
  DataFrameResult,
  TimeSeries,
  TimeSeriesPoint,
  LabeledValue,
} from '../api/types';

/**
 * Build a label string from a frame's field labels, using legendFormat
 * if provided, or falling back to joining label key=value pairs.
 */
function buildLabel(
  labels: Record<string, string> | undefined,
  legendFormat?: string,
): string {
  if (!labels) return '';
  if (legendFormat) {
    return legendFormat.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (_, key) => labels[key] ?? '',
    );
  }
  return Object.entries(labels)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
}

/**
 * Transform a Grafana DataFrameResult for a given refId into an array of
 * TimeSeries suitable for visx line/area charts.
 */
export function transformToTimeSeries(
  result: DataFrameResult,
  refId: string,
  legendFormat?: string,
): TimeSeries[] {
  const refResult = result.results[refId];
  if (!refResult?.frames?.length) return [];

  const output: TimeSeries[] = [];

  for (const frame of refResult.frames) {
    const { schema, data } = frame;
    if (!data?.values?.length || data.values.length < 2) continue;

    const timeIdx = schema.fields.findIndex(f => f.type === 'time');
    const valueIdx = schema.fields.findIndex(
      (f, i) => i !== timeIdx && (f.type === 'number' || f.type === 'float64'),
    );
    if (timeIdx === -1 || valueIdx === -1) continue;

    const times = data.values[timeIdx] as number[];
    const values = data.values[valueIdx] as number[];
    const labels = schema.fields[valueIdx]?.labels;

    const points: TimeSeriesPoint[] = [];
    for (let i = 0; i < times.length; i++) {
      const v = values[i];
      if (v !== null && v !== undefined && Number.isFinite(v)) {
        points.push({ time: times[i], value: v });
      }
    }

    output.push({
      label: buildLabel(labels, legendFormat) || refId,
      points,
    });
  }

  return output;
}

/**
 * Extract a single instant value from a DataFrameResult for a given refId.
 * Returns the last non-null value in the first frame.
 */
export function transformToInstantValue(
  result: DataFrameResult,
  refId: string,
): number | null {
  const refResult = result.results[refId];
  if (!refResult?.frames?.length) return null;

  const frame = refResult.frames[0];
  if (!frame.data?.values?.length || frame.data.values.length < 2) return null;

  const timeIdx = frame.schema.fields.findIndex(f => f.type === 'time');
  const valueIdx = frame.schema.fields.findIndex(
    (f, i) => i !== timeIdx && (f.type === 'number' || f.type === 'float64'),
  );
  if (valueIdx === -1) return null;

  const values = frame.data.values[valueIdx] as number[];
  for (let i = values.length - 1; i >= 0; i--) {
    if (values[i] !== null && values[i] !== undefined && Number.isFinite(values[i])) {
      return values[i];
    }
  }
  return null;
}

/**
 * Transform a DataFrameResult into labeled values for pie/donut charts.
 * Expects one frame per series, label derived from field labels.
 */
export function transformToLabeledValues(
  result: DataFrameResult,
  refId: string,
  legendFormat?: string,
): LabeledValue[] {
  const refResult = result.results[refId];
  if (!refResult?.frames?.length) return [];

  const output: LabeledValue[] = [];

  for (const frame of refResult.frames) {
    const { schema, data } = frame;
    if (!data?.values?.length || data.values.length < 2) continue;

    const timeIdx = schema.fields.findIndex(f => f.type === 'time');
    const valueIdx = schema.fields.findIndex(
      (f, i) => i !== timeIdx && (f.type === 'number' || f.type === 'float64'),
    );
    if (valueIdx === -1) continue;

    const values = data.values[valueIdx] as number[];
    const lastValue = values[values.length - 1];
    if (lastValue === null || lastValue === undefined || !Number.isFinite(lastValue)) continue;

    const labels = schema.fields[valueIdx]?.labels;
    output.push({
      label: buildLabel(labels, legendFormat) || refId,
      value: lastValue,
    });
  }

  return output;
}
